import { useQuery } from "@tanstack/react-query";
import { Config, Contacts } from "@/api/endpoints";

export interface Attribution {
  sourcer_user_id: string | null;
  sourcer_contact_id: string | null;
  closer_user_id: string | null;
}

interface Props {
  value: Attribution;
  onChange: (patch: Partial<Attribution>) => void;
  disabled?: boolean;
  className?: string;
  /** Resolved names from the deal, so a label still shows if that person has
   *  since been deactivated (and so dropped out of the pickers). */
  sourcerName?: string | null;
  closerName?: string | null;
}

/** Sourcer + closer pickers. The sourcer is a teammate OR a broker contact; the
 *  closer is a teammate. Both are labels only — they don't grant deal access. */
export default function AttributionFields({
  value,
  onChange,
  disabled = false,
  className = "input",
  sourcerName,
  closerName,
}: Props) {
  const teamQuery = useQuery({ queryKey: ["owners"], queryFn: () => Config.listOwners() });
  const brokersQuery = useQuery({
    queryKey: ["contacts", "brokers"],
    queryFn: () => Contacts.list({ kind: "broker_contact" }),
  });
  const team = teamQuery.data ?? [];
  const brokers = brokersQuery.data ?? [];

  // The sourcer select encodes which table the id belongs to: "user:<id>" / "contact:<id>".
  const sourcerValue = value.sourcer_user_id
    ? `user:${value.sourcer_user_id}`
    : value.sourcer_contact_id
      ? `contact:${value.sourcer_contact_id}`
      : "";

  const onSourcer = (v: string) => {
    const [kind, id] = v.split(":");
    onChange({
      sourcer_user_id: kind === "user" ? id : null,
      sourcer_contact_id: kind === "contact" ? id : null,
    });
  };

  const missingUser = (id: string | null) => !!id && !team.some((u) => u.id === id);
  const missingBroker = (id: string | null) => !!id && !brokers.some((c) => c.id === id);

  const samePerson = !!value.sourcer_user_id && value.sourcer_user_id === value.closer_user_id;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label" htmlFor="deal-sourcer">
            Sourcer
          </label>
          <select
            id="deal-sourcer"
            className={className}
            disabled={disabled}
            value={sourcerValue}
            onChange={(e) => onSourcer(e.target.value)}
          >
            <option value="">—</option>
            <optgroup label="Team">
              {team.map((u) => (
                <option key={u.id} value={`user:${u.id}`}>
                  {u.name}
                </option>
              ))}
              {teamQuery.isSuccess && missingUser(value.sourcer_user_id) && (
                <option value={`user:${value.sourcer_user_id}`}>
                  {sourcerName ?? "Former teammate"}
                </option>
              )}
            </optgroup>
            {(brokers.length > 0 || value.sourcer_contact_id) && (
              <optgroup label="Brokers">
                {brokers.map((c) => (
                  <option key={c.id} value={`contact:${c.id}`}>
                    {c.name}
                  </option>
                ))}
                {brokersQuery.isSuccess && missingBroker(value.sourcer_contact_id) && (
                  <option value={`contact:${value.sourcer_contact_id}`}>
                    {sourcerName ?? "Broker"}
                  </option>
                )}
              </optgroup>
            )}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="deal-closer">
            Closer
          </label>
          <select
            id="deal-closer"
            className={className}
            disabled={disabled}
            value={value.closer_user_id ?? ""}
            onChange={(e) => onChange({ closer_user_id: e.target.value || null })}
          >
            <option value="">—</option>
            {team.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
            {teamQuery.isSuccess && missingUser(value.closer_user_id) && (
              <option value={value.closer_user_id ?? ""}>{closerName ?? "Former teammate"}</option>
            )}
          </select>
        </div>
      </div>
      {samePerson && <p className="text-xs text-mut-500">Sourced and closed by the same person.</p>}
    </div>
  );
}
